import React, { useEffect, useState } from "react";
import Footer from "../common/Footer";
import Header from "../common/header";
import Subbanner from "./Banner";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchGamesByCategory,
  fetchHtml5Categories,
} from "../api/html5GamesApi";
import RoundLoader from "../common/RoundLoader";
import Sidebar from "./SideBar";
import { url } from "../api/url";
import { useTranslation } from "react-i18next";

interface Game {
  _id?: string;
  name: string;
  title?: string;
  image?: string;
  isFallback?: boolean;
  thumbImage?: {
    default?: string;
  };
}

interface Category {
  _id: string;
  name: string;
}

interface GameListSectionProps {
  categoryName?: string;
  games?: Game[];
  onBack?: () => void;
}

const toSlug = (str = "") =>
  str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const GameListSection: React.FC<GameListSectionProps> = ({
  categoryName: categoryNameProp,
  games: gamesProp,
  onBack,
}) => {
  const { slug } = useParams<{ slug: string }>();
  const [games, setGames] = useState<Game[]>(gamesProp || []);
  const [categoryName, setCategoryName] = useState(categoryNameProp || "");
  const [categoryId, setCategoryId] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!gamesProp);
  const [skip, setSkip] = useState(0);
  const limit = 15;
  const [hasMore, setHasMore] = useState(!gamesProp);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const loadCategoryGames = async (reset = false, resolvedId = "") => {
    try {
      if (!reset) setLoading(true);
      const idToUse = resolvedId || categoryId;
      const gameData = await fetchGamesByCategory(idToUse, skip, limit);
      const newGames: Game[] = gameData?.list || [];

      if (reset) {
        setGames(newGames);
      } else {
        setGames((prev) => [...prev, ...newGames]);
      }

      if (newGames.length < limit) setHasMore(false);
    } catch (err) {
      console.error("Failed to load games", err);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    if (gamesProp) return; // skip fetching if games are passed via props

    const init = async () => {
      try {
        setInitialLoading(true);
        setSkip(0);
        setHasMore(true);

        const catRes = await fetchHtml5Categories({ skip: 0, limit: 100 });
        const allCategories: Category[] = catRes?.list || [];

        const matchedCat = allCategories.find(
          (cat) => toSlug(cat.name) === slug
        );

        if (!matchedCat) {
          setCategoryName("Unknown Category");
          setGames([]);
          setHasMore(false);
          setInitialLoading(false);
          return;
        }

        setCategoryName(matchedCat.name);
        setCategoryId(matchedCat._id);
        await loadCategoryGames(true, matchedCat._id);
      } catch (err) {
        console.error("Error resolving category slug:", err);
        setInitialLoading(false);
      }
    };

    init();
  }, [slug]);

  const handleLoadMore = async () => {
    const newSkip = skip + limit;
    setSkip(newSkip);
    await loadCategoryGames(false);
  };

  return (
    <div className="w-full h-full relative">
      {initialLoading && <RoundLoader size={8} color="#f92c2c" borderWidth={5} />}

      <div
        className={`transition-all duration-300 ${
          initialLoading ? "opacity-20 blur-sm pointer-events-none" : ""
        }`}
      >
        <Header />
        <Subbanner height="30vh" />
        <div className="flex bg-white text-white min-h-screen font-proxima" style={{ background: "#070b28"}}>
          <Sidebar />
          <div className="p-4 max-w-screen-xl mx-auto min-h-screen text-[#ffffff]">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-xl font-semibold">
                {categoryName} - {t("category.allGames")}
              </h1>
              <button
                onClick={onBack ? onBack : () => navigate(-1)}
                className="text-sm px-4 py-1 rounded bg-[#f92c2c] text-white hover:bg-[#d92323]"
              >
                {t("common.back")}
              </button>
            </div>

            <div className="flex flex-wrap gap-4 justify-start">
              {games.map((game, idx) => {
                const imageUrl = `${url.imgUrl}/${
                  game?.isFallback ? game.image : game?.thumbImage?.default || ""
                }`;
                const gameSlug = toSlug(game?.name);

                return (
                  <div
                    key={idx}
                    onClick={() =>
                      navigate(`/game/${gameSlug}`, { state: { game } })
                    }
                    className="rounded-lg overflow-hidden bg-gray-800 w-[140px] sm:w-[18rem] h-[120px] sm:h-[160px] flex-shrink-0 cursor-pointer transition-transform duration-300 transform hover:scale-105 hover:ring-2 hover:ring-white hover:ring-opacity-0 addsquarebox"
                  >
                    <img
                      src={imageUrl}
                      alt={game?.name}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 mt-4 bg-[#f92c2c] text-white rounded hover:bg-[#d92323] transition"
                  disabled={loading}
                >
                  {loading ? t("common.loading") : t("common.loadMore")}
                </button>
              </div>
            )}

            {!hasMore && !initialLoading && (
              <p className="text-center text-[#070b28] mt-4">
                {t("category.noMoreGames")}
              </p>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default GameListSection;
